import {
  NetworkType,
  nettiLiteOfferSpecification,
} from '../../components/OrderSubscriptionSelection/content/NettiLiteOfferSpecification';
import {
  CommercialProductType,
  PbxType,
  Subscription,
  SubscriptionAction,
  SubscriptionDetailsMobile,
  SubscriptionPbxDetails,
  SubscriptionStatusType,
  SubscriptionType,
  TaapiTechnology,
} from '../../generated/api/models.js';
import { paths } from '../constants/pathVariables.js';
import {
  LaitenettiOfferCode,
  LiikkuvaWifiOfferCode,
  LiikkuvaWifiSalesProductOfferCode,
} from '../constants/subscriptionConstants';
import {
  DialogType,
  FiveGMobileBroadbandOfferCode,
  FiveGVoiceOfferCode,
  MobiilivarmenneAddonGroups,
  StatusColor,
  SubscriptionAdditionalServiceStatusEnum,
  SubscriptionAddonCode,
  SubscriptionCategory,
} from '../enums.js';
import {
  activeMsg,
  closedMsg,
  inDeliveryMsg,
  listedNumberMsg,
  notToDirectoryMsg,
  scheduledChangeMsg,
  subChangePendingHeaderMsg,
  t,
  temporarilyClosedMsg,
  unlistedMsg,
} from '../i18n/index.js';
import { suspendedCommercialProductCodes } from './commercialProductUtils.js';
import { isTimestampDateInFuture } from './dateUtils.js';
import { isFeatureEnabled } from './featureFlagUtils.js';
import type {
  AddOn,
  AddOnRulesResponse,
  CommercialProduct,
  CompanyInfoResponse,
  Offer,
  SubscriptionHeader,
} from '../../generated/api/models.js';
import type {
  AssociationRecord,
  DependencyRecord,
} from '../../legacy/cloudsense-add-on-dependency-engine/AddOnManagerOnline';
import type { ConfigState } from '../../public/site/configStore';
import type { DialogParams } from '../types/dialog.js';
import type { SubscriptionActionsState } from '../types/states.js';
import type { SubscriptionStatus } from '../types/subscription.js';

export const ONE_TIME_DEVICE_CHANGE_NOT_ALLOWED = 'Changes not allowed for one time charged products';

export const ELISA_YTT = 'Elisa Yritystietoturva';
export const YTT_MANAGEMENT_SERVICE_URL = 'https://safeavenue.f-secure.com/iframe/-sso/elisaya/';

export const CONSUMER_OMAELISA_LOGIN_URL = 'https://verkkoasiointi.elisa.fi/kirjautuminen';

export const getSubscriptionStatus = (
  subscription?: Subscription | SubscriptionHeader,
  hasPendingActions?: boolean,
  hasScheduledChange?: boolean
): SubscriptionStatus => {
  if (hasScheduledChange) {
    return {
      color: StatusColor.YELLOW,
      text: t.N4LE(scheduledChangeMsg),
      hasPendingActions: true,
      changeOfferAllowed: false,
    };
  }
  if (hasPendingActions) {
    return {
      color: StatusColor.YELLOW,
      text: t.I9KT(subChangePendingHeaderMsg),
      hasPendingActions: true,
      changeOfferAllowed: false,
    };
  }

  switch (subscription?.subscriptionStatus) {
    case SubscriptionStatusType.CREATED:
    case SubscriptionStatusType.IN_ACTIVATION:
      return {
        color: StatusColor.YELLOW,
        text: t.W59S(inDeliveryMsg),
        hasPendingActions: false,
        changeOfferAllowed: false,
      };
    case SubscriptionStatusType.SUSPENDED:
      return {
        color: StatusColor.DARK_BLUE,
        text: t.PC3E(temporarilyClosedMsg),
        hasPendingActions: false,
        changeOfferAllowed: false,
      };
    case SubscriptionStatusType.ACTIVE:
      return {
        color: StatusColor.GREEN,
        text: t.SFRK(activeMsg),
        hasPendingActions: false,
        changeOfferAllowed: true,
      };
    default:
      return {
        color: StatusColor.DARK_BLUE,
        text: t.UFGV(closedMsg),
        hasPendingActions: false,
        changeOfferAllowed: true,
      };
  }
};

export const getSubscriptionStatusForEmployee = (
  subscription?: Subscription,
  subscriptionActions?: SubscriptionAction[]
): SubscriptionStatus => {
  const subAction = subscriptionActions?.find(action => action.subscriptionId === subscription?.subscriptionId);
  const statusEnum = SubscriptionAction.StatusEnum;
  const hasPendingActions =
    (subAction && [statusEnum.PENDING, statusEnum.PENDING_APPROVAL, statusEnum.ERROR].includes(subAction.status)) ||
    (subscription as Subscription)?.details?.asset?.hasPendingActions;

  return getSubscriptionStatus(subscription, hasPendingActions);
};

export const isChangeOfferAllowed = (subscription: Subscription | SubscriptionHeader, hasPendingActions: boolean) => {
  if (hasPendingActions) {
    return false;
  }
  switch (subscription.subscriptionStatus) {
    case SubscriptionStatusType.CREATED:
    case SubscriptionStatusType.IN_ACTIVATION:
    case SubscriptionStatusType.SUSPENDED:
      return false;
    default:
      return true;
  }
};

export const doActionIfNotPendingSubscription = (
  onShowDialog: (params: DialogParams) => void,
  action: () => void,
  hasPendingActions?: boolean
) => (hasPendingActions ? onShowDialog({ type: DialogType.SUBSCRIPTION_ACTION_PENDING }) : action());

export const openSubscriptionDialog = (
  onShowDialog: (params: DialogParams) => void,
  dialogParams: DialogParams,
  hasPendingActions?: boolean
) => onShowDialog(hasPendingActions ? { type: DialogType.SUBSCRIPTION_ACTION_PENDING } : dialogParams);

export const getTaapiTechnologyAsString = (taapiTechnology?: TaapiTechnology): string | undefined => {
  switch (taapiTechnology) {
    case TaapiTechnology.ADSL:
    case TaapiTechnology.ADSL_FTTB:
    case TaapiTechnology.ADSL_FTTC:
      return t.G07Y('ADSL');

    case TaapiTechnology.CRM_KTV:
    case TaapiTechnology.CABLE_MODEM:
      return t.R488('Cable');

    case TaapiTechnology.ETHERNET_FTTB:
    case TaapiTechnology.FTTB_ETH:
    case TaapiTechnology.FTTB_ETH_1G:
    case TaapiTechnology.FTTH:
    case TaapiTechnology.YKI:
    case TaapiTechnology.ETH_FTTB_FIB:
    case TaapiTechnology.YKI_FIB:
      return t.URFP('Optical fibre');

    case TaapiTechnology.VDSL_CO:
    case TaapiTechnology.VDSL2_FTTB:
    case TaapiTechnology.VDSL2_FTTC:
      return t.LG3L('VDSL');

    default:
      return undefined;
  }
};

export const isCommitmentPeriodActive = (endDateTimestamp?: number) =>
  endDateTimestamp ? isTimestampDateInFuture(endDateTimestamp) : false;

export const isInExtensionPeriod = (subscription: Subscription | SubscriptionHeader): boolean =>
  subscription.changeType === 'Extension Period';

export const commercialProductIdForHyllyliittyma = {
  MOBILE: 'a0d2e28a-8ad8-aea3-9929-02aa2498c2fe',
  MOBILE_BROADBAND: '3ca41f56-58b4-c6ca-5b5c-5e492f8b689e',
};

export const isBbOrMbbSubscription = (subscription: Subscription): boolean =>
  subscription.subscriptionType === SubscriptionType.BROADBAND ||
  subscription.subscriptionType === SubscriptionType.MOBILE_BROADBAND;

export const isHyllyLiittyma = (subscription: Subscription): boolean =>
  !!subscription.commercialProductCode && suspendedCommercialProductCodes.includes(subscription.commercialProductCode);

export const isMobileIdAddOn = (addOnGroup?: string): boolean =>
  Object.values(MobiilivarmenneAddonGroups).some(group => group === addOnGroup);

export const isReachabilityWithMobile = (addonCode?: string): boolean =>
  addonCode === SubscriptionAddonCode.REACHABILITY_WITH_MOBILE;

export const mobiilivarmenneNotInUseAddOn: AddOn = {
  addOnCode: SubscriptionAdditionalServiceStatusEnum.NOT_IN_USE,
  addOnGroup: MobiilivarmenneAddonGroups.CERTIFICATE,
  addOnProductName: 'Mobiilivarmenne',
  addOnType: SubscriptionType.MOBILE_CERTIFICATE,
  display: true,
};

export const simCardChangeAllowed = (subscription: Subscription) =>
  subscription.subscriptionStatus !== SubscriptionStatusType.CREATED &&
  subscription.subscriptionStatus !== SubscriptionStatusType.IN_ACTIVATION;

export const hasEsimQrCode = (subscription: Subscription) =>
  Boolean(subscription.details?.mobile?.eSimQrActivationCode);

export const isSubscriptionInSfdc = (subscription: Subscription | SubscriptionHeader) =>
  Boolean(subscription.sourceSystem === Subscription.SourceSystemEnum.SFDC || subscription.migrated);

export const isSubscriptionInTellusOrMipa = (subscription: Subscription | SubscriptionHeader) =>
  Boolean(
    (subscription.sourceSystem === Subscription.SourceSystemEnum.TELLUS ||
      subscription.sourceSystem === Subscription.SourceSystemEnum.MIPA) &&
      !subscription.migrated
  );

export const isSubscriptionInSaunalahti = (subscription: Subscription | SubscriptionHeader) =>
  Boolean(subscription.sourceSystem === Subscription.SourceSystemEnum.JPD && !subscription.migrated);

export const isExternalSubscription = (subscription: Subscription | SubscriptionHeader) =>
  isSubscriptionInTellusOrMipa(subscription) || isSubscriptionInSaunalahti(subscription);

export const isFiveGMbbOffer = (offerGuid = ''): boolean =>
  [
    FiveGMobileBroadbandOfferCode.MBB_300_M,
    FiveGMobileBroadbandOfferCode.MBB_100_M,
    FiveGMobileBroadbandOfferCode.MBB_50_M,
    FiveGMobileBroadbandOfferCode.MBB_10_M,
  ].some(code => code === offerGuid);

export const isFiveGPlusMbbOffer = (offerGuid = ''): boolean =>
  [
    FiveGMobileBroadbandOfferCode.MBB_5G_PLUS_1000_M,
    FiveGMobileBroadbandOfferCode.MBB_5G_PLUS_600_M,
    FiveGMobileBroadbandOfferCode.MBB_5G_PLUS_300_M,
    FiveGMobileBroadbandOfferCode.MBB_5G_PLUS_100_M,
    LiikkuvaWifiOfferCode.MBB_1000_M,
    LiikkuvaWifiOfferCode.MBB_600_M,
    LiikkuvaWifiOfferCode.MBB_300_M,
  ].some(code => code === offerGuid);

export const isFiveGPlusVoiceOffer = (offerGuid = ''): boolean =>
  [
    FiveGVoiceOfferCode.Voice5gPlus1000Mbps,
    FiveGVoiceOfferCode.Voice5gPlus600Mbps,
    FiveGVoiceOfferCode.Voice5gPlus300Mbps,
  ].some(code => code === offerGuid);

export const isFiveGPlusNettiliteOffer = (offerGuid = '') =>
  nettiLiteOfferSpecification()
    .filter(nettilite => nettilite.offerCodes.includes(offerGuid))
    .some(nettiLite => nettiLite.networkType === NetworkType.FIVE_G_PLUS);

export const isFiveGVoiceOffer = (offerGuid = ''): boolean =>
  Object.values(FiveGVoiceOfferCode).includes(offerGuid as FiveGVoiceOfferCode);

export const isLiikkuvaWifiOffer = (offerGuid = ''): boolean =>
  Object.values(LiikkuvaWifiOfferCode).includes(offerGuid as LiikkuvaWifiOfferCode) ||
  Object.values(LiikkuvaWifiSalesProductOfferCode).includes(offerGuid as LiikkuvaWifiSalesProductOfferCode);

export const isFiveGOffer = (offerGuid = ''): boolean => isFiveGMbbOffer(offerGuid) || isFiveGVoiceOffer(offerGuid);

export const isFiveGPlusOffer = (offerGuid = ''): boolean =>
  isFiveGPlusMbbOffer(offerGuid) || isFiveGPlusVoiceOffer(offerGuid);

export const isYttSubscription = (subscriptionSubType?: string) => subscriptionSubType?.includes(ELISA_YTT);

export const containsYttSubscription = (subscriptions?: SubscriptionHeader[] | Subscription[]) =>
  subscriptions ? subscriptions.some(res => isYttSubscription(res.subscriptionSubType)) : false;

export const pendingSubscriptionActionExistsInState = (
  subscriptionId: string,
  subscriptionActionsState: SubscriptionActionsState | null | undefined
) =>
  subscriptionActionsState?.items?.find(pendingAction => pendingAction.subscriptionId === subscriptionId) !== undefined;

export const getSubscriptionBreadcrumbName = (subscription: Subscription) =>
  subscription.subscriptionDisplayId ??
  subscription.details?.mobile?.mobileNumber ??
  subscription.subscriptionUserFriendlyId ??
  subscription.subscriptionId;

export const getSubscriptionUrlId = (subscription: Subscription | SubscriptionHeader) =>
  subscription.subscriptionDisplayId ?? subscription.subscriptionId;

export const isLaitenettiOffer = (offer: Offer) =>
  Object.values(LaitenettiOfferCode).includes(offer.offerCode as LaitenettiOfferCode);

export const isSalesProduct = (commercialProduct?: CommercialProduct) =>
  commercialProduct?.productType === CommercialProductType.SALES_PRODUCT;

export const getDirectoryListingViewOptions = () => [
  {
    displayValue: t.KC80(notToDirectoryMsg),
    value: SubscriptionDetailsMobile.DirectoryListingEnum.NOT_TO_DIRECTORY,
  },
  {
    displayValue: t.ZGKE(unlistedMsg),
    value: SubscriptionDetailsMobile.DirectoryListingEnum.SECRET,
  },
  {
    displayValue: t.SCL4(listedNumberMsg),
    value: SubscriptionDetailsMobile.DirectoryListingEnum.CUSTOM,
  },
  {
    displayValue: t.SCL4(listedNumberMsg),
    value: SubscriptionDetailsMobile.DirectoryListingEnum.PUBLIC_DIRECTORY,
  },
];

export const getAddonAssociationsAndDependencies = (
  subscription?: Subscription,
  addOnRules?: AddOnRulesResponse,
  addOnRulesMobilePbx?: AddOnRulesResponse
) => {
  const ringServiceLevelName =
    subscription?.details?.mobile?.pbxConfiguration?.pbxConfigurationDetails.ringServiceLevelName;
  const associations =
    subscription && ringServiceLevelName
      ? (addOnRulesMobilePbx?.associations[0] as AssociationRecord[])
      : (addOnRules?.associations[0] as AssociationRecord[]);
  const dependencies =
    subscription && ringServiceLevelName
      ? (addOnRulesMobilePbx?.dependencies[0] as DependencyRecord[])
      : (addOnRules?.dependencies[0] as DependencyRecord[]);
  return { associations, dependencies };
};

export const getAvailableAddOnCodes = (associations?: AssociationRecord[], commercialProductId?: string): string[] =>
  associations
    ?.filter(association => commercialProductId && association.cspmb__price_item__c === commercialProductId)
    ?.map(association => association.cspmb__add_on_price_item__r.guid__c) ?? [];

const findFromPbxSolutionsByType = (
  companyInfo: CompanyInfoResponse,
  subscriptionType: SubscriptionType
): Subscription[] | undefined => {
  return companyInfo?.pbxSolutions
    ? companyInfo.pbxSolutions.filter(
        item => item.subscriptionType === subscriptionType && item.subscriptionStatus === SubscriptionStatusType.ACTIVE
      )
    : undefined;
};

export const isAttachingRingValid = (subscription: Subscription, companyInfo: CompanyInfoResponse): boolean => {
  if (subscription.details?.mobile?.pbxConfiguration) {
    return false;
  }
  const ringSolutionSubscriptions: Subscription[] | undefined =
    findFromPbxSolutionsByType(companyInfo, SubscriptionType.MOBILE_PBX) ?? [];
  return ringSolutionSubscriptions?.length > 0;
};

export const getConfiguredPbxSolution = (subscription: Subscription, pbxSolutions: Subscription[]) =>
  subscription.details!.mobile &&
  subscription.details!.mobile.pbxConfiguration &&
  pbxSolutions!.find(
    pbxSolution => pbxSolution.subscriptionId === subscription.details!.mobile!.pbxConfiguration!.pbxSolutionId
  );

export const companyHasMobilePbxLite = (pbxSolutions: Subscription[]) =>
  pbxSolutions.some(({ subscriptionType }) => subscriptionType === SubscriptionType.MOBILE_PBX_LITE);

export const shouldDisplayPbxLiteSubscription = (subscription: Subscription, pbxSolutions: Subscription[]) =>
  companyHasMobilePbxLite(pbxSolutions) &&
  getConfiguredPbxSolution(subscription, pbxSolutions) &&
  subscription.details!.mobile!.pbxConfiguration!.pbxConfigurationDetails.pbxType ===
    SubscriptionPbxDetails.PbxTypeEnum.VAKIO;

export const shouldDisplayAttachToRing = (subscription: Subscription, companyInfo?: CompanyInfoResponse) =>
  companyInfo &&
  isAttachingRingValid(subscription, companyInfo) &&
  isSubscriptionInSfdc(subscription) &&
  subscription.subscriptionType === SubscriptionType.MOBILE;

export const shouldDisplayAttachToVakio = (subscription: Subscription, pbxSolutions: Subscription[]) =>
  companyHasMobilePbxLite(pbxSolutions) &&
  subscription.compatiblePbxTypes &&
  subscription.compatiblePbxTypes.includes(PbxType.MOBILE_PBX_LITE) &&
  !subscription.details!.mobile!.pbxConfiguration &&
  isSubscriptionInSfdc(subscription) &&
  subscription.subscriptionType === SubscriptionType.MOBILE;

export const shouldDisplayMobileReachabilityAddon = (
  config: ConfigState,
  subscription: Subscription,
  pbxSolutions: Subscription[]
) =>
  isFeatureEnabled(config.featureFlags.showReachabilityWithMobile) &&
  shouldDisplayPbxLiteSubscription(subscription, pbxSolutions);

export const getSubCategoryFromPath = (path: string) => {
  const pathname = decodeURI(path);

  if (pathname.startsWith(paths.PS_MOBILE_SUBSCRIPTIONS)) {
    return SubscriptionCategory.VOICE;
  }
  if (pathname.startsWith(paths.PS_BROADBAND_SUBSCRIPTIONS)) {
    return SubscriptionCategory.BROADBAND;
  }
  if (pathname.startsWith(paths.PS_DEVICES)) {
    return SubscriptionCategory.DEVICE;
  }
  if (pathname.startsWith(paths.PS_SERVICES)) {
    return SubscriptionCategory.SERVICE;
  }
  return undefined;
};

export const isMobileBroadbandSubscription = (subscription?: Subscription) =>
  subscription?.subscriptionType === SubscriptionType.MOBILE_BROADBAND;

export const isNettiLiteSubscription = (subscription: Subscription) =>
  subscription?.subscriptionName?.toLocaleLowerCase().includes('netti lite');
